// Petitioner details capture modal - expungement-narrative + fee-waiver
// fields. Shown when the paralegal clicks "Personal details" on the
// offender page. Saves to POST /api/jobs/:id/petitioner-details.
//
// Scope: ONLY the fields that feed the Form 9 narrative (employer,
// family, programs, why-this-matters, certifications, contributions),
// the SBI number, and the fee-waiver inputs (monthly income, assets,
// dependents).
//
// Address/city/state/zip/phone/email/sex live in ProfileEditModal
// (the "Edit profile" button on the hero) - that one is the single
// source of truth for those. Editing them here previously created
// two places to change the same data; we removed the overlap.
//
// Optional throughout - every field can be skipped. Empty strings are
// stripped server-side before storage so we never end up with `""` in
// PDF form fields.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { runWithToast } from "../lib/downloadToast";

export interface PetitionerDetails {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
  sbiNumber?: string;
  employer?: string;
  jobTitle?: string;
  employerStartYear?: string;
  family?: string;
  programs?: string;
  certifications?: string;
  whyExpungementMatters?: string;
  positiveContributions?: string;
  monthlyIncome?: number;
  totalAssets?: number;
  numDependents?: number;
}

interface Props {
  jobId: string;
  petitionerName: string;
  onClose: () => void;
  onSaved: () => void;
}

export function PetitionerDetailsModal({ jobId, petitionerName, onClose, onSaved }: Props) {
  const [loaded, setLoaded] = useState(false);
  const [details, setDetails] = useState<PetitionerDetails>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load any previously-saved details so the user can edit instead of
  // re-entering from scratch.
  useEffect(() => {
    fetch(`/api/jobs/${jobId}/petitioner-details`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: { details: PetitionerDetails | null }) => {
        if (data.details) setDetails(data.details);
      })
      .catch(() => { /* ignore - start with empty form */ })
      .finally(() => setLoaded(true));
  }, [jobId]);

  // Esc closes; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const set = <K extends keyof PetitionerDetails>(k: K, v: PetitionerDetails[K]) => {
    setDetails((d) => ({ ...d, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only send fields THIS modal owns. The server's POST handler
      // does a key-level merge, so omitting a field preserves whatever
      // ProfileEditModal (or another saver) wrote last. Sending the
      // fields the modal no longer edits would re-write our stale
      // snapshot of them on top of any newer save.
      const FIELDS_OWNED_HERE = [
        "sbiNumber",
        "employer", "jobTitle", "employerStartYear",
        "family", "programs", "certifications",
        "whyExpungementMatters", "positiveContributions",
        "monthlyIncome", "totalAssets", "numDependents",
      ] as const;
      const body: Record<string, unknown> = {};
      for (const k of FIELDS_OWNED_HERE) {
        const v = details[k as keyof typeof details];
        if (v !== undefined) body[k] = v;
      }
      // Coerce numeric fields - the form gives us strings.
      for (const k of ["monthlyIncome", "totalAssets", "numDependents"] as const) {
        const raw = body[k];
        if (raw === "" || raw == null) {
          delete body[k];
        } else if (typeof raw === "string") {
          const n = Number(raw);
          if (Number.isFinite(n)) body[k] = n; else delete body[k];
        }
      }
      await runWithToast({
        label: "Saving petitioner details",
        successDetail: "Saved",
        run: async () => {
          const r = await fetch(`/api/jobs/${jobId}/petitioner-details`, {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          });
          if (!r.ok) throw new Error(`Server returned ${r.status}`);
        },
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Petitioner details"
        className="modal-card modal-card-wide"
      >
        <header className="modal-hero">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="modal-hero-close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="modal-hero-eyebrow">Expungement Packet · Personal Details</div>
          <h2 className="modal-hero-title">{petitionerName}</h2>
          <p
            className="mt-2"
            style={{ color: "var(--color-hero-text-muted, rgba(255,255,255,0.82))", fontSize: 13, lineHeight: 1.5, marginBottom: 0 }}
          >
            Optional. Filled-in details enrich the Form 9 narrative and pre-fill the
            official PDFs (address, phone, fee waiver). Every field is optional,
            skip anything you don't have. Click <strong>Save</strong>, then re-download
            the Expungement packet to get the regenerated version.
          </p>
        </header>

        {/* Scrollable form */}
        <div className="modal-body">
          {!loaded ? (
            <div className="text-sm text-[color:var(--color-text-muted)]">Loading…</div>
          ) : (
            <div className="space-y-7">
              <Section
                title="Identifier"
                hint="Address, phone, email, sex live on the Edit Profile modal (top of the hero), those are shared across the whole record. SBI is expungement-only, so it stays here."
              >
                <Grid>
                  <TextInput label="SBI Number" value={details.sbiNumber} onChange={(v) => set("sbiNumber", v)} placeholder="From State Police record list" />
                </Grid>
              </Section>

              <Section title="Employment">
                <Grid>
                  <TextInput label="Employer" value={details.employer} onChange={(v) => set("employer", v)} placeholder="Newark Beth Israel Medical Center" />
                  <TextInput label="Job title" value={details.jobTitle} onChange={(v) => set("jobTitle", v)} placeholder="Maintenance Tech II" />
                  <TextInput label="Employer start year" value={details.employerStartYear} onChange={(v) => set("employerStartYear", v)} placeholder="2018" />
                </Grid>
              </Section>

              <Section title="Family, programs, certifications" hint="Free text. Specifics beat adjectives, &lsquo;completed Inspira IOP in 2018&rsquo; beats &lsquo;is now a different person&rsquo;.">
                <TextArea
                  label="Family / household"
                  value={details.family}
                  onChange={(v) => set("family", v)}
                  placeholder="Two kids (ages 8 and 11). Primary caregiver for mother who has dementia."
                />
                <TextArea
                  label="Programs completed"
                  value={details.programs}
                  onChange={(v) => set("programs", v)}
                  placeholder="Inspira intensive outpatient (Sept 2018, 90 days). Anger management with Catholic Charities (2019). NA weekly since 2018."
                />
                <TextArea
                  label="Certifications / training"
                  value={details.certifications}
                  onChange={(v) => set("certifications", v)}
                  placeholder="OSHA 30 (2020). ServSafe Manager (2022). Forklift cert (2023)."
                />
              </Section>

              <Section title="Why expungement matters now" hint="Concrete forward-looking need: what does the expungement actually unlock? Judges grant relief that does work.">
                <TextArea
                  label="Why this matters"
                  value={details.whyExpungementMatters}
                  onChange={(v) => set("whyExpungementMatters", v)}
                  placeholder="Offered a promotion to facilities supervisor that requires a background check. / Trying to move family to a co-op that won't approve with my record. / Applying to nursing program at Bergen Community College."
                />
                <TextArea
                  label="Other positive contributions / context"
                  value={details.positiveContributions}
                  onChange={(v) => set("positiveContributions", v)}
                  placeholder="Coaches little league. Mentors with NJRC's reentry youth program. Volunteers at food pantry."
                />
              </Section>

              <Section title="Fee waiver (optional)" hint="If captured, the official CN-11208 fee waiver in the packet will be pre-filled. Eligibility: ≤150% Federal Poverty Level + ≤$2,500 spendable assets.">
                <Grid>
                  <NumberInput label="Monthly income (net)" value={details.monthlyIncome} onChange={(v) => set("monthlyIncome", v)} placeholder="2200" />
                  <NumberInput label="Total assets" value={details.totalAssets} onChange={(v) => set("totalAssets", v)} placeholder="800" />
                  <NumberInput label="Number of dependents" value={details.numDependents} onChange={(v) => set("numDependents", v)} placeholder="2" step={1} />
                </Grid>
              </Section>

              {error && (
                <div className="rounded-lg border border-[color:var(--color-rose-hi)] bg-[color:var(--color-rose-soft)] px-3 py-2 text-sm text-[color:var(--color-rose-hi)]">
                  Save failed: {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="modal-footer">
          <button onClick={onClose} type="button" className="btn btn-ghost">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !loaded}
            type="button"
            className="btn btn-primary"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[14px] font-semibold uppercase tracking-[0.16em] text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      {hint && (
        <p className="mt-1 text-[12px] italic text-[color:var(--color-text-dim)]">{hint}</p>
      )}
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>;
}

function TextInput({ label, value, onChange, placeholder }: {
  label: string; value?: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <label className="block">
      <div className="label mb-1 text-[12px]">{label}</div>
      <input
        type="text"
        className="input w-full"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function NumberInput({ label, value, onChange, placeholder, step = 0.01 }: {
  label: string; value?: number; onChange: (v: number | undefined) => void; placeholder?: string; step?: number;
}) {
  return (
    <label className="block">
      <div className="label mb-1 text-[12px]">{label}</div>
      <input
        type="number"
        step={step}
        className="input w-full"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        placeholder={placeholder}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder }: {
  label: string; value?: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <label className="block">
      <div className="label mb-1 text-[12px]">{label}</div>
      <textarea
        rows={3}
        className="input w-full"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}
